import { FileMetaData } from 'hyparquet';

import { ParquetStageMetric } from './instrumentation';

export type ParquetRow = {
  X: number;
  Y: number;
  label: string;
} & Record<string, any>;

/**
 * One decoded in-memory column. This is not the Parquet file's physical column
 * encoding; it is the browser representation produced after hyparquet decodes
 * the requested columns.
 *
 * Numeric and boolean values use typed arrays so they can be transferred from
 * the worker and uploaded to WebGL without creating an object per row. Repeated
 * strings use dictionary indexes for the same reason. `values` is the fallback
 * for columns that cannot be represented by one of those compact encodings.
 */
export type ParquetColumn =
  | { kind: 'number'; values: Float64Array; validity?: Uint8Array }
  | { kind: 'boolean'; values: Uint8Array; validity?: Uint8Array }
  | { kind: 'dictionary'; values: Uint32Array; dictionary: string[]; validity?: Uint8Array }
  | { kind: 'values'; values: unknown[] };

/**
 * Column-oriented browser data for large visualization workloads.
 *
 * Parquet is always columnar on disk. The distinction represented by
 * `outputFormat` is what happens after parsing:
 *
 * - `rows` returns `Array<{ X, Y, ...columns }>` and is convenient for tables,
 *   arbitrary row transforms, density processing, and code that frequently
 *   needs complete records.
 * - `columnar` returns transferable typed arrays and dictionary-coded columns.
 *   Use it when consumers scan a few columns across many rows or can pass
 *   attributes directly to Deck.gl/WebGL. It avoids retaining hundreds of
 *   thousands of JavaScript objects and reduces structured-clone, GC, and GPU
 *   attribute-building work.
 *
 * Columnar access is index based. Reconstruct a row only at an interaction
 * boundary such as picking; repeatedly reconstructing every row removes most
 * of the benefit. Both formats contain the same requested values.
 */
export interface ColumnarParquetData {
  length: number;
  positions: Float64Array;
  columns: Record<string, ParquetColumn>;
  rowContext: Record<string, any>;
}

export interface ParquetFile {
  rows: ParquetRow[];
  columnarData?: ColumnarParquetData;
  metadata: Pick<FileMetaData, 'schema' | 'num_rows'>;
  columnsQueried: string[];
}

export interface ParquetMetadataReadRequest {
  parquetFileUrl: string;
  byteLength?: number;
  minSize?: number;
}

export interface ParquetRowsReadRequest extends ParquetMetadataReadRequest {
  columnsForQuery?: string[];
  additionalRowContext?: Record<string, any>;
  /**
   * Selects the decoded in-memory shape, not the Parquet parser or file format.
   * Defaults to `rows`. Choose `columnar` only when downstream consumers support
   * indexed columns/typed Deck.gl attributes; otherwise choose `rows`.
   */
  outputFormat?: 'rows' | 'columnar';
}

export const getParquetColumnGroupKey = (
  columnsGroupForHeatmap: string[] | undefined,
  outputFormat?: ParquetRowsReadRequest['outputFormat']
): string => `${columnsGroupForHeatmap?.join(',') ?? ''}${outputFormat === 'columnar' ? '::columnar' : ''}`;

interface ParquetWorkerRequestBase {
  requestId: number;
  generation: number;
  instrumentationEnabled?: boolean;
}

export interface ParquetWorkerMetadataRequest extends ParquetWorkerRequestBase {
  kind: 'metadata';
  payload: ParquetMetadataReadRequest;
}

export interface ParquetWorkerRowsRequest extends ParquetWorkerRequestBase {
  kind: 'rows';
  payload: ParquetRowsReadRequest & { baseFile?: ParquetFile };
}

export interface ParquetWorkerBaseRowsChunk extends ParquetWorkerRequestBase {
  kind: 'base-rows-chunk';
  chunkIndex: number;
  rows: ParquetRow[];
}

export interface ParquetWorkerCancelRequest extends ParquetWorkerRequestBase {
  kind: 'cancel';
}

export type ParquetWorkerRequest =
  | ParquetWorkerMetadataRequest
  | ParquetWorkerRowsRequest
  | ParquetWorkerBaseRowsChunk
  | ParquetWorkerCancelRequest;

interface ParquetWorkerResponseBase {
  requestId: number;
  generation: number;
}

export interface ParquetWorkerMetadataSuccess extends ParquetWorkerResponseBase {
  kind: 'metadata-success';
  metadata: Pick<FileMetaData, 'schema' | 'num_rows'>;
}

export interface ParquetWorkerRowsChunk extends ParquetWorkerResponseBase {
  kind: 'rows-chunk';
  chunkIndex: number;
  rows: ParquetRow[];
}

export interface ParquetWorkerRowsComplete extends ParquetWorkerResponseBase {
  kind: 'rows-complete';
  chunkCount: number;
  metadata: Pick<FileMetaData, 'schema' | 'num_rows'>;
  columnsQueried: string[];
}

export interface ParquetWorkerColumnarComplete extends ParquetWorkerResponseBase {
  kind: 'columnar-complete';
  columnarData: ColumnarParquetData;
  metadata: Pick<FileMetaData, 'schema' | 'num_rows'>;
  columnsQueried: string[];
}

export interface ParquetWorkerCancelled extends ParquetWorkerResponseBase {
  kind: 'cancelled';
}

export interface ParquetWorkerError extends ParquetWorkerResponseBase {
  kind: 'error';
  message: string;
}

export interface ParquetWorkerMetric extends ParquetWorkerResponseBase {
  kind: 'metric';
  metric: ParquetStageMetric;
}

export type ParquetWorkerResponse =
  | ParquetWorkerMetadataSuccess
  | ParquetWorkerRowsChunk
  | ParquetWorkerRowsComplete
  | ParquetWorkerColumnarComplete
  | ParquetWorkerCancelled
  | ParquetWorkerError
  | ParquetWorkerMetric;
